import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, MapPin, Phone, Linkedin, Instagram, Youtube } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/4e491698-f834-49a8-a1e5-8fd3efa88feb/images/d77c0364065b8ec483b9e6915ff1d30475de2851aefe5560f16a14318cc351be.png";
import { API_URL } from '../config/api';
const API = `${API_URL}/api`;

const footerLinks = {
  produit: [
    { label: 'Equipe Digitale', href: '/equipe-digitale' },
    { label: 'Machine a Clients', href: '/machine-clients' },
    { label: 'Transformation', href: '/transformation' },
    { label: 'Tarifs', href: '/tarifs' },
  ],
  entreprise: [
    { label: 'A propos', href: '/a-propos' },
    { label: 'Blog', href: '/blog' },
    { label: 'Contact', href: '/contact' },
    { label: 'FAQ', href: '/faq' },
  ],
  legal: [
    { label: 'Mentions legales', href: '/mentions-legales' },
    { label: 'Politique de confidentialite', href: '/confidentialite' },
    { label: "Conditions d'utilisation", href: '/conditions' },
  ],
};

export default function Footer() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [subscribeSuccess, setSubscribeSuccess] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, { email });
      toast.success(response.data.message);
      setEmail('');
      setSubscribeSuccess(true);
      setTimeout(() => setSubscribeSuccess(false), 5000);
    } catch (error) {
      toast.error("Une erreur est survenue. Veuillez reessayer.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <footer className="bg-slate-50 border-t border-slate-200" data-testid="footer">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Main Footer */}
        <div className="py-10 sm:py-16 grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-5 gap-6 sm:gap-10">
          {/* Brand Column */}
          <div className="col-span-2 sm:col-span-2 lg:col-span-2">
            <Link to="/" className="inline-block mb-4 sm:mb-6">
              <img 
                src={LOGO_URL} 
                alt="BatiAssist" 
                className="h-10 sm:h-12 w-auto"
              />
            </Link>
            <p className="text-rm-text-secondary text-xs sm:text-sm leading-relaxed mb-4 sm:mb-6 max-w-sm">
              L'assistant digital des artisans du batiment. CRM, agents IA et assistants humains 
              pour organiser ton entreprise et développer ton activité.
            </p>
            <div className="flex items-center gap-3 sm:gap-4">
              <a 
                href="https://linkedin.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-ba-primary/10 flex items-center justify-center hover:bg-ba-primary/20 transition-colors"
                data-testid="footer-linkedin"
              >
                <Linkedin className="w-4 h-4 sm:w-5 sm:h-5 text-ba-primary" />
              </a>
              <a 
                href="https://instagram.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-ba-primary/10 flex items-center justify-center hover:bg-ba-primary/20 transition-colors"
                data-testid="footer-instagram"
              >
                <Instagram className="w-4 h-4 sm:w-5 sm:h-5 text-ba-primary" />
              </a>
              <a 
                href="https://youtube.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-ba-primary/10 flex items-center justify-center hover:bg-ba-primary/20 transition-colors"
                data-testid="footer-youtube"
              >
                <Youtube className="w-4 h-4 sm:w-5 sm:h-5 text-ba-primary" />
              </a>
            </div>
          </div>

          {/* Produit */}
          <div>
            <h4 className="font-semibold text-rm-text mb-3 sm:mb-4 text-sm sm:text-base">Produit</h4>
            <ul className="space-y-2 sm:space-y-3">
              {footerLinks.produit.map((link, index) => (
                <li key={index}>
                  <Link 
                    to={link.href}
                    className="text-rm-text-secondary hover:text-ba-primary text-xs sm:text-sm transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Entreprise */}
          <div>
            <h4 className="font-semibold text-rm-text mb-3 sm:mb-4 text-sm sm:text-base">Entreprise</h4>
            <ul className="space-y-2 sm:space-y-3">
              {footerLinks.entreprise.map((link, index) => (
                <li key={index}>
                  <Link 
                    to={link.href}
                    className="text-rm-text-secondary hover:text-ba-primary text-xs sm:text-sm transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div className="col-span-2 sm:col-span-2 lg:col-span-1">
            <h4 className="font-semibold text-rm-text mb-3 sm:mb-4 text-sm sm:text-base">Newsletter</h4>
            <p className="text-rm-text-secondary text-xs sm:text-sm mb-3 sm:mb-4">
              Recevez nos conseils pour mieux organiser ton entreprise.
            </p>
            {subscribeSuccess ? (
              <div className="bg-green-100 border border-green-200 rounded-xl p-3 sm:p-4 text-center" data-testid="newsletter-success">
                <p className="text-green-700 font-medium text-sm">Inscription reussie !</p>
                <p className="text-green-600 text-xs mt-1">Bienvenue chez BatiAssist</p>
              </div>
            ) : (
              <form onSubmit={handleNewsletterSubmit} className="space-y-2 sm:space-y-3">
                <Input
                  type="email"
                  placeholder="Ton email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-white border-slate-200 text-rm-text placeholder:text-slate-400 rounded-xl text-sm"
                  data-testid="footer-newsletter-email"
                />
                <Button 
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-ba-secondary hover:bg-ba-secondary/90 text-white rounded-xl text-sm"
                  data-testid="footer-newsletter-submit"
                >
                  {isLoading ? 'Inscription...' : "S'inscrire"}
                </Button>
              </form>
            )}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-4 sm:py-6 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
          <p className="text-rm-text-secondary text-xs sm:text-sm text-center sm:text-left">
            © {new Date().getFullYear()} BatiAssist. Tous droits reserves.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-6">
            {footerLinks.legal.map((link, index) => (
              <Link 
                key={index}
                to={link.href}
                className="text-rm-text-secondary hover:text-ba-primary text-xs sm:text-sm transition-colors"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
